/** Parse API decimal strings without using `Number()` for money math when comparing. */
export function parseDecimalString(value: string | undefined | null): number | null {
  if (value == null || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

/**
 * POC parity (formatMoney L6153 / getCurrencyLocaleForRegion
 * L1943): render with the currency SYMBOL ("$0.57", "€0.43"), not the ISO code
 * suffix. POC forces "en-US" for USD so the glyph is "$" (not "US$") and uses
 * the browser default locale for EUR (an en-* browser yields "€0.43").
 */
const CURRENCY_LOCALE: Record<string, string | undefined> = { USD: "en-US" };

/**
 * Round half-away-from-zero to `digits` decimals. Pre-rounding here pins
 * the displayed value to "round half up" regardless of the locale-formatter's
 * own rounding mode, so wire values like "2.3270" always render as "2.33"
 * (not "2.32").
 */
function roundHalfAwayFromZero(n: number, digits: number): number {
  const k = 10 ** digits;
  return Math.sign(n) * Math.round(Math.abs(n) * k) / k;
}

export function formatMoney(
  value: string | number | null | undefined,
  currencyCode?: string,
): string {
  const n =
    typeof value === "number" ? value : parseDecimalString(String(value ?? ""));
  if (n == null) return "—";
  const rounded = roundHalfAwayFromZero(n, 2);
  if (currencyCode) {
    try {
      return rounded.toLocaleString(CURRENCY_LOCALE[currencyCode], {
        style: "currency",
        currency: currencyCode,
      });
    } catch {
      // Unknown/invalid ISO code — fall through to a plain 2dp number.
    }
  }
  return rounded.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/** Whole-number % shown on comparison KPI cards (POC Math.round(ratio * 100)). */
export function roundUtilRatioToDisplayPercent(ratio: number): number {
  return Math.max(0, Math.round(ratio * 100));
}

export function formatPercent(value: string | undefined | null): string {
  const n = parseDecimalString(value);
  if (n == null) return "—";
  return `${(n * 100).toLocaleString(undefined, { maximumFractionDigits: 1 })}%`;
}

export function pickModeCost(
  mode: "shared" | "dedicated",
  fields: {
    shared?: string;
    dedicated?: string;
    fallback?: string;
  },
): string | null {
  if (mode === "dedicated") {
    const d = parseDecimalString(fields.dedicated);
    if (d != null) return fields.dedicated ?? null;
  }
  const s = parseDecimalString(fields.shared);
  if (s != null) return fields.shared ?? null;
  return fields.fallback ?? null;
}

export function compareDecimalStrings(a: string, b: string): number {
  const na = parseDecimalString(a);
  const nb = parseDecimalString(b);
  if (na == null && nb == null) return 0;
  if (na == null) return 1;
  if (nb == null) return -1;
  return na - nb;
}
